import React, { useState } from 'react';
import {
  X,
  ShoppingCart,
  Banknote,
  CreditCard,
  Ticket,
  FileText,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Check,
  AlertTriangle,
  Delete,
  Undo2,
  CheckCircle2,
} from 'lucide-react';
import { formatCurrency } from '../../utils/currency';
import { PROMO_CODES } from '../../mocks/PromoCodes.mocks';
import { PromoCodeType } from '../../types/promo.types';
import './PaymentModal.css';

export type PaymentMethodType = 'cash' | 'card' | 'marcial_card' | 'gift_card' | 'invoice';

export interface PaymentModalProps {
  total: number;
  isClientSelected: boolean;
  selectedPromo: PromoCodeType | null;
  onSelectPromo: (promo: PromoCodeType | null) => void;
  view: string;
  itemsCount: number;
  discountsTotal: number;
  promoDiscount: number;
  shippingCost?: number;
  onClose: () => void;
  onConfirm: (method: PaymentMethodType) => void;
  enableCashChangeCalculator?: boolean;
  /** Metodo ya elegido, para no perderlo al volver de elegir cliente. */
  initialMethod?: PaymentMethodType | null;
  onRequestClientSelection?: (method: PaymentMethodType | null) => void;
  /** Si la factura se envia por email al cobrar. El selector solo sale si se pasa. */
  sendEmail?: boolean;
  onSendEmailChange?: (value: boolean) => void;
  /** A donde iria, o '' si todavia no se sabe (se pedira al confirmar). */
  emailTarget?: string;
  /** Solicitud de DER: solo sale con cliente seleccionado y si se pasa el callback. */
  derRequested?: boolean;
  onDerToggle?: (value: boolean) => void;
  /** Se pidio el DER pero faltan los datos del viajero: no se puede confirmar el pago. */
  derIncomplete?: boolean;
  /** Reabre el formulario del viajero. */
  onDerEdit?: () => void;
}

export function PaymentModal({
  total,
  isClientSelected,
  selectedPromo,
  onSelectPromo,
  view,
  itemsCount,
  discountsTotal,
  promoDiscount,
  shippingCost = 0,
  onClose,
  onConfirm,
  enableCashChangeCalculator = true,
  initialMethod = null,
  onRequestClientSelection,
  sendEmail = false,
  onSendEmailChange,
  emailTarget = '',
  derRequested = false,
  onDerToggle,
  derIncomplete = false,
  onDerEdit,
}: PaymentModalProps) {
  const [selectedMethod, setSelectedMethod] = useState<PaymentMethodType | null>(initialMethod);
  const [promoStartIndex, setPromoStartIndex] = useState(0);
  const [paymentStartIndex, setPaymentStartIndex] = useState(0);
  const [showCashKeypad, setShowCashKeypad] = useState(false);
  const [enteredCash, setEnteredCash] = useState<string>('');

  const handleKeypadPress = (key: string) => {
    if (key === 'C') {
      setEnteredCash('');
    } else if (key === '⌫' || key === 'delete' || key === 'backspace') {
      setEnteredCash(prev => prev.slice(0, -1));
    } else if (key === '.') {
      setEnteredCash(prev => {
        if (prev.includes('.')) return prev;
        if (prev === '') return '0.';
        return prev + '.';
      });
    } else {
      setEnteredCash(prev => {
        if (prev.includes('.') && prev.split('.')[1].length >= 2) {
          return prev;
        }
        if (prev.length >= 8) return prev;
        return prev + key;
      });
    }
  };

  const handlePrevPromo = () => {
    setPromoStartIndex(prev => Math.max(0, prev - 1));
  };

  const handleNextPromo = () => {
    setPromoStartIndex(prev => Math.min(PROMO_CODES.length - 4, prev + 1));
  };

  const visiblePromoCodes = PROMO_CODES.slice(promoStartIndex, promoStartIndex + 4);

  const paymentOptions: Array<{
    id: PaymentMethodType;
    icon: React.ReactNode;
    label: string;
    iconClass: string;
  }> = [
    { id: 'cash', icon: <Banknote size={32} />, label: 'Efectivo', iconClass: 'payment-method-icon-wrap--cash' },
    { id: 'card', icon: <CreditCard size={32} />, label: 'Tarjeta', iconClass: 'payment-method-icon-wrap--card' },
    { id: 'marcial_card', icon: <CreditCard size={32} />, label: 'Tarjeta Marcial Pons', iconClass: 'payment-method-icon-wrap--marcial' },
    { id: 'gift_card', icon: <Ticket size={32} />, label: 'Tarjeta o Cheque Regalo', iconClass: 'payment-method-icon-wrap--gift' },
    ...(isClientSelected
      ? [
          {
            id: 'invoice' as PaymentMethodType,
            icon: <FileText size={32} />,
            label: 'Contra Factura',
            iconClass: 'payment-method-icon-wrap--invoice',
          },
        ]
      : []),
  ];

  const maxPaymentIndex = Math.max(0, paymentOptions.length - 4);
  const safePaymentStartIndex = Math.min(paymentStartIndex, maxPaymentIndex);
  const visiblePaymentOptions = paymentOptions.slice(safePaymentStartIndex, safePaymentStartIndex + 4);

  const handlePrevPayment = () => {
    setPaymentStartIndex(prev => Math.max(0, prev - 1));
  };

  const handleNextPayment = () => {
    setPaymentStartIndex(prev => Math.min(maxPaymentIndex, prev + 1));
  };

  const cashVal = parseFloat(enteredCash) || 0;
  const change = cashVal - total;
  const hasEnough = cashVal >= total - 0.001;

  return (
    <div className="payment-modal-overlay">
      <div className="payment-modal-container">
        <button
          type="button"
          onClick={onClose}
          className="payment-modal-close-btn"
          aria-label="Cerrar modal de pago"
        >
          <X size={24} />
        </button>

        <div className="payment-modal-content">
          {!showCashKeypad && (
            <div className="payment-modal-header">
              <span className="payment-modal-badge">Finalizar Operación</span>
              <div className="payment-modal-total-wrapper">
                <p className="payment-modal-total-label">Total a Pagar</p>
                <p className="payment-modal-total-amount">
                  {formatCurrency(total)}
                </p>
              </div>
            </div>
          )}

          {showCashKeypad ? (
            <div className="cash-keypad-grid-wrapper">
              {/* Left Column: Calculation Breakdown */}
              <div className="cash-keypad-calc-col">
                <div>
                  <div className="cash-keypad-calc-header">
                    <span className="cash-keypad-badge">Pago en Efectivo</span>
                    <h3 className="cash-keypad-calc-title">Cálculo de Cambio</h3>
                  </div>

                  <div className="cash-keypad-cards-stack">
                    {/* Importe Compra */}
                    <div className="cash-card-total">
                      <div>
                        <span className="cash-card-label cash-card-label--gray">Total de la Compra</span>
                        <span className="cash-card-total-val">{formatCurrency(total)}</span>
                      </div>
                      <div className="cash-card-icon-box">
                        <ShoppingCart size={20} />
                      </div>
                    </div>

                    {/* Dinero Entregado */}
                    <div className="cash-card-entered">
                      <div>
                        <span className="cash-card-label cash-card-label--orange">Efectivo Entregado</span>
                        <div className="cash-card-entered-row">
                          <span className="cash-card-entered-val">
                            {enteredCash ? formatCurrency(parseFloat(enteredCash)) : formatCurrency(0)}
                          </span>
                          {!enteredCash && (
                            <span className="cash-card-prompt-hint">Pulse dígitos...</span>
                          )}
                        </div>
                      </div>
                      <div className="cash-card-entered-icon-box">
                        <Banknote size={24} />
                      </div>
                    </div>

                    {/* Cambio a Devolver */}
                    <div
                      className={`cash-card-change ${
                        hasEnough ? 'cash-card-change--enough' : 'cash-card-change--not-enough'
                      }`}
                    >
                      <div>
                        <span className="cash-card-label">
                          {hasEnough ? 'Cambio a Devolver' : 'Falta Entregar'}
                        </span>
                        <span className="cash-card-change-val">
                          {hasEnough ? formatCurrency(change) : formatCurrency(total - cashVal)}
                        </span>
                      </div>
                      <div
                        className={`cash-card-change-icon-box ${
                          hasEnough ? 'cash-card-change-icon-box--enough' : 'cash-card-change-icon-box--not-enough'
                        }`}
                      >
                        {hasEnough ? <Check size={24} /> : <AlertTriangle size={24} />}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Billetes Rápidos */}
                <div className="cash-shortcuts-section">
                  <p className="cash-shortcuts-label">Accesos Rápidos</p>
                  <div className="cash-shortcuts-grid">
                    {[
                      { label: 'Exacto', value: total },
                      { label: '5 €', value: 5 },
                      { label: '10 €', value: 10 },
                      { label: '20 €', value: 20 },
                      { label: '50 €', value: 50 },
                      { label: '100 €', value: 100 },
                    ].map((shortcut, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => setEnteredCash(shortcut.value.toString())}
                        className="cash-shortcut-btn"
                      >
                        {shortcut.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Right Column: Numeric Direct Keypad */}
              <div className="cash-keypad-input-col">
                <div>
                  <p className="cash-keypad-title">Teclado Numérico Directo</p>
                  <div className="cash-keypad-grid">
                    {['7', '8', '9', '4', '5', '6', '1', '2', '3', 'C', '0', '.'].map(key => {
                      const isClear = key === 'C';
                      return (
                        <button
                          key={key}
                          type="button"
                          onClick={() => handleKeypadPress(key)}
                          className={`cash-keypad-key ${
                            isClear ? 'cash-keypad-key--clear' : 'cash-keypad-key--num'
                          }`}
                        >
                          {key}
                        </button>
                      );
                    })}
                  </div>

                  {/* Backspace utility */}
                  <button
                    type="button"
                    disabled={!enteredCash}
                    onClick={() => handleKeypadPress('⌫')}
                    className="cash-backspace-btn"
                  >
                    <Delete size={20} />
                    Borrar
                  </button>
                </div>

                {/* Bottom actions block inside Cash Keypad */}
                <div className="cash-actions-row">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCashKeypad(false);
                      setEnteredCash('');
                    }}
                    className="cash-back-btn"
                  >
                    <Undo2 size={16} />
                    Volver
                  </button>

                  <button
                    type="button"
                    onClick={() => onConfirm('cash')}
                    className="cash-confirm-btn"
                  >
                    Confirmar Pago
                    <CheckCircle2 size={20} />
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Economic Summary (Bento Grid) */}
              <div className="payment-summary-bento">
                <div className="payment-summary-cell">
                  <span className="payment-summary-label payment-summary-label--gray">Nº Libros</span>
                  <span className="payment-summary-value payment-summary-value--navy">{itemsCount}</span>
                </div>

                <div className="payment-summary-cell payment-summary-cell--bordered">
                  <span className="payment-summary-label payment-summary-label--red">Descuento</span>
                  <span
                    className={`payment-summary-value ${
                      discountsTotal + promoDiscount > 0
                        ? 'payment-summary-value--red'
                        : 'payment-summary-value--muted'
                    }`}
                  >
                    {discountsTotal + promoDiscount > 0
                      ? `-${formatCurrency(discountsTotal + promoDiscount)}`
                      : formatCurrency(0)}
                  </span>
                </div>

                <div className="payment-summary-cell payment-summary-cell--bordered">
                  <span className="payment-summary-label payment-summary-label--gray">IVA Total (4%)</span>
                  <span className="payment-summary-value payment-summary-value--navy">
                    {formatCurrency(total * 0.04)}
                  </span>
                </div>

                <div className="payment-summary-cell payment-summary-cell--bordered payment-summary-cell--highlight">
                  <span className="payment-summary-label payment-summary-label--green">
                    {view === 'returns' ? 'A Devolver' : 'Total Pagar'}
                  </span>
                  <strong className="payment-summary-value payment-summary-value--green">
                    {formatCurrency(total)}
                  </strong>
                </div>
              </div>

              {/* Promo Codes Section */}
              {view === 'sale' ? (
                <div className="payment-carousel-row">
                  <button
                    type="button"
                    onClick={handlePrevPromo}
                    disabled={promoStartIndex === 0}
                    className="payment-carousel-nav-btn"
                    title="Promoción Anterior"
                  >
                    <ChevronLeft size={20} />
                  </button>

                  <div className="payment-carousel-grid">
                    {visiblePromoCodes.map(p => {
                      const isSelected = selectedPromo?.code === p.code;
                      return (
                        <button
                          key={p.code}
                          type="button"
                          onClick={() => onSelectPromo(isSelected ? null : p)}
                          className={`payment-promo-btn ${
                            isSelected ? 'payment-promo-btn--active' : ''
                          }`}
                        >
                          <div className="payment-promo-btn-header">
                            <Sparkles size={12} />
                            <span className="payment-promo-btn-label">
                              {p.label.split('(')[0].trim()}
                            </span>
                          </div>
                          <span
                            className={`payment-promo-btn-percent ${
                              isSelected
                                ? 'payment-promo-btn-percent--active'
                                : 'payment-promo-btn-percent--inactive'
                            }`}
                          >
                            -{p.discountPercent}%
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    type="button"
                    onClick={handleNextPromo}
                    disabled={promoStartIndex + 4 >= PROMO_CODES.length}
                    className="payment-carousel-nav-btn"
                    title="Siguiente Promoción"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              ) : (
                <div className="payment-promos-na">
                  Promociones no aplicables en devoluciones
                </div>
              )}

              {/* Payment Methods Carousel */}
              <div className="payment-carousel-row">
                <button
                  type="button"
                  onClick={handlePrevPayment}
                  disabled={safePaymentStartIndex === 0}
                  className="payment-carousel-nav-btn"
                  title="Medio de Pago Anterior"
                >
                  <ChevronLeft size={20} />
                </button>

                <div className="payment-carousel-grid">
                  {visiblePaymentOptions.map(opt => {
                    const isActive = selectedMethod === opt.id;
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setSelectedMethod(opt.id)}
                        className={`payment-method-card ${
                          isActive ? 'payment-method-card--active' : ''
                        }`}
                      >
                        <div className={`payment-method-icon-wrap ${isActive ? opt.iconClass : ''}`}>
                          {opt.icon}
                        </div>
                        <span
                          className={`payment-method-label ${
                            isActive ? 'payment-method-label--active' : 'payment-method-label--inactive'
                          }`}
                        >
                          {opt.label}
                        </span>

                        {isActive && (
                          <div className="payment-method-indicator">
                            <div className="payment-method-dot" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={handleNextPayment}
                  disabled={safePaymentStartIndex + 4 >= paymentOptions.length}
                  className="payment-carousel-nav-btn"
                  title="Siguiente Medio de Pago"
                >
                  <ChevronRight size={20} />
                </button>
              </div>

              {onSendEmailChange && (
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.25rem', cursor: 'pointer', fontSize: '0.85rem', color: '#3D405B' }}
                >
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => onSendEmailChange(e.target.checked)}
                    data-testid="pm-send-email"
                  />
                  <span>
                    Enviar la factura por email
                    {sendEmail && (emailTarget ? ` a ${emailTarget}` : ' (se pedirá el email)')}
                  </span>
                </label>
              )}

              {onDerToggle && isClientSelected && (
                <label
                  style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.25rem', cursor: 'pointer', fontSize: '0.85rem', color: '#3D405B' }}
                >
                  <input
                    type="checkbox"
                    checked={derRequested}
                    onChange={(e) => onDerToggle(e.target.checked)}
                    data-testid="pm-request-der"
                  />
                  <span>Solicitar DER (devolución de IVA al viajero)</span>
                  {derRequested && onDerEdit && (
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); onDerEdit(); }}
                      style={{ marginLeft: 'auto', border: 0, background: 'none', color: derIncomplete ? '#EF4444' : '#E07A5F', fontWeight: 700, cursor: 'pointer' }}
                    >
                      {derIncomplete ? 'Faltan los datos del viajero' : 'Editar datos'}
                    </button>
                  )}
                </label>
              )}

              {/* Bottom Confirm Action Button */}
              <button
                type="button"
                disabled={!selectedMethod || derIncomplete}
                onClick={() => {
                  // El cliente es opcional: el ticket se crea siempre y la
                  // venta sin identificar se factura al cliente por defecto.
                  // Solo "Contra Factura" lo exige, y esa opcion ya no aparece
                  // sin cliente seleccionado.
                  if (selectedMethod === 'cash') {
                    if (enableCashChangeCalculator) {
                      setShowCashKeypad(true);
                      setEnteredCash('');
                    } else {
                      onConfirm('cash');
                    }
                  } else if (selectedMethod) {
                    onConfirm(selectedMethod);
                  }
                }}
                className="payment-confirm-btn"
              >
                {selectedMethod ? (
                  <span className="payment-confirm-btn-content">
                    CONFIRMAR PAGO
                    <CheckCircle2 size={24} className="payment-confirm-icon-pulse" />
                  </span>
                ) : (
                  <span>SELECCIONA MÉTODO</span>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
